import { ChangeEvent } from 'react';
import {
  Box, Button, Checkbox, FormControlLabel, MenuItem, TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ReferralEligibility, ReferralStatus } from '../../../enums/referral.enum';
import { ReferralsFilters as ReferralsFiltersValue } from '../../../@types/Referral';
import STRING_KEYS from '../../../language/keys';
import { REFERRALS_PAGE_FILTERS, toFilterParam } from '../referrals.utils';
import style from './style';

const ALL_OPTION = '';

type ReferralsFiltersProps = {
  filters: ReferralsFiltersValue;
  onChangeFilter: (filterName: string, newValue: string[]) => void;
  onTaxReportClick?: () => void;
};

const ReferralsFilters = ({
  filters,
  onChangeFilter,
  onTaxReportClick,
}: ReferralsFiltersProps): JSX.Element => {
  const { t: translate } = useTranslation();

  const handleSelectChange = (filterName: string) => (event: ChangeEvent<HTMLInputElement>) => {
    onChangeFilter(filterName, event.target.value ? [event.target.value] : []);
  };

  const handleToggleChange = (filterName: string) => (event: ChangeEvent<HTMLInputElement>) => {
    onChangeFilter(filterName, toFilterParam(event.target.checked));
  };

  const toggles = [
    {
      key: REFERRALS_PAGE_FILTERS.UNLINKED,
      label: translate(STRING_KEYS.REFERRALS.FILTER_UNLINKED),
      checked: !!filters.unlinked,
    },
    {
      key: REFERRALS_PAGE_FILTERS.PAYOUT_FAILED,
      label: translate(STRING_KEYS.REFERRALS.FILTER_PAYOUT_FAILED),
      checked: !!filters.payoutFailed,
    },
    {
      key: REFERRALS_PAGE_FILTERS.NO_HUBSPOT_STATUS,
      label: translate(STRING_KEYS.REFERRALS.FILTER_NO_HUBSPOT_STATUS),
      checked: !!filters.noHubspotStatus,
    },
  ];

  return (
    <Box sx={ style.container.sx }>
      <TextField
        select
        size="small"
        label={ translate(STRING_KEYS.REFERRALS.FILTER_STATUS) }
        value={ filters.status || ALL_OPTION }
        onChange={ handleSelectChange(REFERRALS_PAGE_FILTERS.STATUS) }
        sx={ style.select.sx }
      >
        <MenuItem value={ ALL_OPTION }>{ translate(STRING_KEYS.REFERRALS.FILTER_ALL) }</MenuItem>
        { Object.values(ReferralStatus).map((status) => (
          <MenuItem key={ status } value={ status }>
            { translate(STRING_KEYS.REFERRAL_STATUSES[status]) }
          </MenuItem>
        )) }
      </TextField>
      <TextField
        select
        size="small"
        label={ translate(STRING_KEYS.REFERRALS.FILTER_ELIGIBILITY) }
        value={ filters.eligibility || ALL_OPTION }
        onChange={ handleSelectChange(REFERRALS_PAGE_FILTERS.ELIGIBILITY) }
        sx={ style.select.sx }
      >
        <MenuItem value={ ALL_OPTION }>{ translate(STRING_KEYS.REFERRALS.FILTER_ALL) }</MenuItem>
        { Object.values(ReferralEligibility).map((eligibility) => (
          <MenuItem key={ eligibility } value={ eligibility }>
            { translate(STRING_KEYS.REFERRAL_ELIGIBILITIES[eligibility]) }
          </MenuItem>
        )) }
      </TextField>
      { toggles.map((toggle) => (
        <FormControlLabel
          key={ toggle.key }
          label={ toggle.label }
          control={ (
            <Checkbox
              checked={ toggle.checked }
              onChange={ handleToggleChange(toggle.key) }
            />
          ) }
        />
      )) }
      { onTaxReportClick && (
        <Box sx={ style.actions.sx }>
          <Button variant="outlined" onClick={ onTaxReportClick }>
            { translate(STRING_KEYS.REFERRALS.TAX_REPORT_BUTTON) }
          </Button>
        </Box>
      ) }
    </Box>
  );
};

ReferralsFilters.defaultProps = {
  onTaxReportClick: undefined,
};

export default ReferralsFilters;
